import { PlusCircleOutlined } from '@ant-design/icons'
import {
  ResponseFormula,
  ResponseFormulaToken,
  ResponseFormulaTokenOperatorTypeEnum,
  ResponseFormulaTokenTypeEnum,
  ResponseTypeEnum,
  TemplateNodeSchema,
  TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { Button, Modal, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useSiblingQuestions } from 'src/features/templateEdit/common/useSiblingQuestions'
import { EditResponseFormulaDrawerBlock } from 'src/features/templateEdit/editResponseFormula/EditResponseFormulaDrawerBlock'
import { Danger } from 'src/features/theme/KdsThemeColor'
import styled from 'styled-components'

interface Props {
  /**
   * 編集対象の質問ノードの id。
   */
  nodeId: number
  /**
   * 編集対象の質問ノードに設定されている計算式 (編集前の状態)
   */
  responseFormula?: ResponseFormula
  onClose: () => void
  onSave: (questionPatch: Partial<TemplateQuestion>) => void
}

// モーダル上で計算式を編集する際のブロック1つ分を表現したもの
export type FormulaBlock = {
  // 1番目のブロックはオペレーターを持たない。2番目以降のブロックはオペレーターを持つ。
  operator?: ResponseFormulaToken
  // 質問or定数の別。オプショナルになっているが実のところ必須のはず。
  questionOrConstant?: ResponseFormulaToken
}

const { Text } = Typography

const AddButton = styled(Button)`
  width: 100%;
  margin-bottom: 8px;
`

const ErrorText = styled(Text)`
  color: ${Danger};
`

const validate = (
  tokens: ResponseFormulaToken[],
  numberNodes: TemplateNodeSchema[],
): string => {
  if (tokens.length < 2) {
    return '式は2つ以上設定する必要があります'
  }
  if (
    tokens.filter(t => t.type === ResponseFormulaTokenTypeEnum.QUESTION)
      .length === 0
  ) {
    return '質問を1つ以上設定する必要があります'
  }

  const numberNodeMapByUUID = numberNodes.reduce((map, node) => {
    if (node.uuid) {
      map.set(node.uuid, node)
    }
    return map
  }, new Map<string, TemplateNodeSchema>())

  let editableQuestionCount = 0
  for (const token of tokens) {
    switch (token.type) {
      case ResponseFormulaTokenTypeEnum.QUESTION: {
        if (!token.questionNodeUUID) {
          return '設定値が不正です'
        }
        const numberNode = numberNodeMapByUUID.get(token.questionNodeUUID)
        if (!numberNode) {
          return '設定値が不正です'
        }
        if (!numberNode.question?.answerOption) {
          editableQuestionCount++
        }
        break
      }
      case ResponseFormulaTokenTypeEnum.CONSTANT: {
        if (token.constant === undefined || token.constant === null) {
          return '設定値が不正です'
        }
      }
    }
  }
  if (editableQuestionCount === 0) {
    return '記録時に入力可能な質問を1つ以上含めてください'
  }

  return ''
}

const EditResponseFormulaDrawer = (props: Props) => {
  const { onClose, nodeId, responseFormula, onSave } = props

  const [blocks, setBlocks] = useState<FormulaBlock[]>([{}])
  const [errorMessage, setErrorMessage] = useState<string>('')

  const numberNodes = useSiblingQuestions(nodeId, ResponseTypeEnum.NUMBER)

  useEffect(() => {
    if (responseFormula?.tokens) {
      const createBlocks: FormulaBlock[] = []
      let operator: ResponseFormulaToken = {}
      for (const token of responseFormula.tokens) {
        switch (token.type) {
          case ResponseFormulaTokenTypeEnum.QUESTION:
          case ResponseFormulaTokenTypeEnum.CONSTANT: {
            const block: FormulaBlock = {
              operator: operator,
              questionOrConstant: token,
            }
            createBlocks.push(block)
            break
          }
          case ResponseFormulaTokenTypeEnum.OPERATOR: {
            operator = token
            break
          }
        }
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
      setBlocks(createBlocks)
      setErrorMessage('')
    }
  }, [responseFormula?.tokens])

  return (
    <Modal
      title="計算式の設定"
      maskClosable={false}
      cancelText="キャンセル"
      onCancel={() => {
        onClose()
      }}
      okText="登録"
      onOk={() => {
        const newTokens: ResponseFormulaToken[] = []
        blocks.forEach((block, index) => {
          if (index !== 0) {
            if (block.operator) {
              newTokens.push(block.operator)
            }
          }
          if (block.questionOrConstant) {
            newTokens.push(block.questionOrConstant)
          }
        })
        const newResponseFormula = {
          ...responseFormula,
          tokens: newTokens,
        }
        const errorMessage = validate(newTokens, numberNodes)
        setErrorMessage(errorMessage)
        if (!errorMessage) {
          // 計算式タイプの質問に計算式を設定する
          onSave({ responseFormulas: [newResponseFormula] })
          onClose()
        }
      }}
      open={true}
      width="80%"
    >
      {blocks.map((block, index) => {
        return (
          <EditResponseFormulaDrawerBlock
            key={index}
            numberNodes={numberNodes}
            block={block}
            isFirstBlock={index === 0}
            changeBlock={change => {
              const newBlock = blocks.map((b, i) => {
                return i === index ? change : b
              })
              setBlocks([...newBlock])
            }}
            deleteBlock={() => {
              blocks.splice(index, 1)
              setBlocks([...blocks])
            }}
          />
        )
      })}
      <AddButton
        type="dashed"
        icon={<PlusCircleOutlined />}
        onClick={() => {
          setBlocks([
            ...blocks,
            {
              operator: {
                type: ResponseFormulaTokenTypeEnum.OPERATOR,
                operator: ResponseFormulaTokenOperatorTypeEnum.PLUS,
              },
              questionOrConstant: {
                type: ResponseFormulaTokenTypeEnum.QUESTION,
              },
            },
          ])
        }}
      >
        式を追加
      </AddButton>
      {!!errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </Modal>
  )
}

export default EditResponseFormulaDrawer
